import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { getLegFacets } from '@/api/orders.api';
import { useActivePharmacy } from '@/auth/active-pharmacy';
import { useAuth } from '@/auth/auth-context';
import { usePharmacyFeatures } from '@/auth/features';
import { queryClient } from '@/lib/query-client';
import { queryKeys } from '@/lib/query-keys';

/**
 * How many orders are waiting to be accepted, for the chrome.
 *
 * The same request and the SAME CACHE KEY the order queue uses, which is the
 * whole point: a badge counting one thing while the table under it shows
 * another is worse than no badge, and sharing the key means they cannot
 * disagree - accepting an order on the queue page invalidates it for the
 * sidebar too.
 *
 * Returns 0 rather than undefined once loaded, and undefined while it has
 * never loaded, so the chrome can tell "none waiting" from "do not know yet"
 * and render nothing at all in the second case.
 */
export function useNewOrderCount(): number | undefined {
  const { can } = useAuth();
  const { pharmacyId } = useActivePharmacy();
  const features = usePharmacyFeatures();

  /*
   * The same two gates the Orders nav row is behind. Without them a
   * counter-only shop, or a user who cannot read orders, would sit polling a
   * route the server is going to refuse - and the sidebar would carry a badge
   * for a link that is not even rendered.
   */
  const allowed = Boolean(pharmacyId) && can('order:read:any') && features.marketplace;

  const { data } = useQuery({
    queryKey: queryKeys.orders.queueFacets(pharmacyId!),
    queryFn: () => getLegFacets(pharmacyId!),
    enabled: allowed,
    /*
     * Twenty seconds, matching the queue page. A push arrives sooner when the
     * shop has allowed notifications; this is the floor for everybody else,
     * and for the tablet whose permission was refused months ago.
     */
    refetchInterval: 20_000,
    refetchIntervalInBackground: true,
  });

  if (!allowed) return 0;
  return data ? (data.byStatus.PENDING ?? 0) : undefined;
}

/**
 * Refetch the moment a push lands, rather than at the next poll.
 *
 * The service worker posts to every open tab when it shows a notification.
 * Without this the lock screen could say "New order" while the badge beside it
 * still read zero for another nineteen seconds - and a badge that is behind
 * the notification is one people learn to distrust.
 *
 * Mounted once, at the shell.
 */
export function usePushRefresh(): void {
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

    const onMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'push') return;
      // Everything order- and prescription-shaped: the counts the badges read,
      // and the list behind whichever tab happens to be open. A push is either
      // a new order or a new prescription, and refetching both is cheap.
      void queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.prescriptions.all });
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);
}
